import React, { useEffect, useState } from 'react';
import TemplateFilePreview from './TemplateFilePreview';
import { resolveTemplateType } from '../templates';
import { useTheme } from '../hooks/useTheme';

export default function TemplatePreviewModal({ template, onClose, onUse }) {
  const { mode } = useTheme();
  const s = getModalStyles(mode);
  const [visible, setVisible] = useState(false);
  const templateType = template.type || resolveTemplateType(template.title);
  const hasPreview = !!templateType;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const handleUse = () => {
    setVisible(false);
    setTimeout(() => onUse(template), 200);
  };

  return (
    <div style={{ ...s.backdrop, opacity: visible ? 1 : 0 }} onClick={handleClose}>
      <div
        style={{
          ...s.modal,
          ...(hasPreview ? s.modalFull : {}),
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={s.header}>
          <div style={s.titleRow}>
            <span style={s.emoji}>{template.emoji}</span>
            <span style={s.title}>{template.title}</span>
            {hasPreview && <span style={s.liveBadge}>Live Preview</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {hasPreview && (
              <button className="tpm-use" style={s.useBtn} onClick={handleUse}>Use Template →</button>
            )}
            <button className="tpm-close" style={s.closeBtn} onClick={handleClose}>✕</button>
          </div>
        </div>

        {hasPreview && (
          <div style={s.previewWrap}>
            <TemplateFilePreview templateType={templateType} style={{ minHeight: '100%' }} />
          </div>
        )}

        {!hasPreview && (
          <div style={s.footer}>
            <button className="tpm-use" style={s.useBtn} onClick={handleUse}>Use Template →</button>
            <button className="tpm-cancel" style={s.cancelBtn} onClick={handleClose}>Cancel</button>
          </div>
        )}
      </div>

      <style>{`
        .tpm-use:hover { opacity: 0.88 !important; }
        .tpm-cancel:hover { background: ${mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'} !important; }
        .tpm-close:hover { background: ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'} !important; color: ${mode === 'light' ? '#1e293b' : '#e2e8f0'} !important; }
      `}</style>
    </div>
  );
}

const getModalStyles = (mode) => ({
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: mode === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px 16px',
    transition: 'opacity 0.2s ease',
  },
  modal: {
    background: mode === 'light'
      ? 'linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)'
      : 'linear-gradient(160deg, #1e1e38 0%, #1a1a2e 100%)',
    border: mode === 'light'
      ? '1px solid rgba(99,102,241,0.2)'
      : '1px solid rgba(99,102,241,0.25)',
    borderRadius: 18,
    padding: '24px 24px 20px',
    width: '100%', maxWidth: 420,
    boxShadow: mode === 'light'
      ? '0 24px 64px rgba(0,0,0,0.15), 0 0 0 1px rgba(99,102,241,0.08)'
      : '0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    display: 'flex', flexDirection: 'column', gap: 16,
  },
  modalFull: {
    maxWidth: '92vw', width: '92vw',
    height: '90vh', maxHeight: '90vh',
    padding: '18px 20px 20px',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 10 },
  emoji: { fontSize: 22 },
  title: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' },
  liveBadge: {
    fontSize: 10, fontWeight: 700, color: '#22c55e',
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)',
    padding: '3px 9px', borderRadius: 20, letterSpacing: '0.06em',
  },
  closeBtn: {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
    background: 'transparent', border: 'none',
    color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s, color 0.15s',
  },
  previewWrap: {
    flex: 1, borderRadius: 12, overflow: 'hidden',
    border: mode === 'light' ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.07)',
    boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.08)',
    minHeight: 0,
  },
  footer: { display: 'flex', gap: 10, flexShrink: 0 },
  useBtn: {
    padding: '9px 18px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none', borderRadius: 9,
    color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'opacity 0.15s', flexShrink: 0,
  },
  cancelBtn: {
    padding: '11px 18px',
    background: 'transparent',
    border: mode === 'light' ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.1)',
    borderRadius: 9, color: 'var(--text-muted)', fontSize: 13,
    cursor: 'pointer', transition: 'background 0.15s',
  },
});
